import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ImgA from "./assets/voices/side_a.jpg";
import ImgB from "./assets/voices/side_b.jpg";
import { ReactComponent as CheckIcon } from "./assets/voices/icon-check.svg";
import clsx from "clsx";
import apiInstance from "../../shared/utils/axios";
import qs from "qs";
import { AxiosRequestHeaders } from "axios";

function useQueryParam(name: string): string | null {
  return new URLSearchParams(useLocation().search).get(name);
}

const sideMap = {
  inner: "inner",
  audience: "audience",
} as const;

const imgMap = {
  inner: {
    curtain: "1",
    record: "2",
  },
  audience: {
    meet: "1",
    passion: "2",
    seat: "3",
  },
} as const;

const VoicesPage = () => {
  const navigate = useNavigate();
  const rawSide = useQueryParam("side");
  const rawImg = useQueryParam("img");
  const sideKey = rawSide as keyof typeof imgMap;
  const imgKey = rawImg as keyof (typeof imgMap)[typeof sideKey];
  const pageSide = sideMap[rawSide as keyof typeof sideMap];
  const imgIdx = imgMap[sideKey]?.[imgKey];

  const [isOpen, setIsOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!email || !availSubmit) return;

    const eventParameters = {
      page_side: pageSide,
      img_idx: imgIdx,
      email: email,
    };

    apiInstance
      .post("/oapi/survey/pa", undefined, qs.stringify(eventParameters), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Access-Control-Allow-Origin": `http://localhost:3001`,
          "Access-Control-Allow-Credentials": true,
        },
      })
      .then((res) => {
        try {
          // console.log("res ? ", res);
          if (res.data) {
            setEmail("");
            setSubmitted(true);
          }
        } catch (e) {
          console.log("submit error", e);
        }
      })
      .catch((err) => {
        console.log("submit api error", err);
      });
  }

  useEffect(() => {
    if (!pageSide || !imgIdx) {
      navigate("/", { replace: true });
    }
  }, [pageSide, imgIdx, navigate]);

  const isPageInner = pageSide === "inner";

  const desc = isPageInner
    ? "Looking for a place to document your team’s journey?"
    : "Stay connected with your audience, wherever you are.";

  const sns = isPageInner
    ? "A social network for performing arts teams"
    : "A social network connecting performers and audiences";

  const validEmail = isValidEmail(email);
  const availSubmit = validEmail && agreed;

  return (
    <>
      <div
        className="grid w-full h-screen bg-[#F7F7FA] cursor-default justify-items-center"
        style={{ fontFamily: "Pretendard" }}
      >
        <div className="w-full max-w-[375px] min-w-[320px] h-screen pt-[2.5rem] pb-[1.5rem] px-[1rem] bg-white text-center relative z-10">
          {submitted ? (
            <div className="space-y-4 h-full max-h-[630px] pb-6 place-items-center content-center">
              <div className="size-[5rem] bg-[#6B7EFF] rounded-full content-center justify-items-center">
                <CheckIcon width={48} height={48} />
              </div>

              <div className="space-y-[0.12rem]">
                <h1 className="text-[#374259] text-[1.375rem] leading-[2rem] font-bold">
                  You’re on the list!
                </h1>
                <p className="text-[#545B77]">We look forward to seeing you soon.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-[0.25rem]">
                <h1 className="text-2xl font-bold text-[#374259]">
                  Welcome, creatives!
                </h1>
                <h1 className="text-[0.875rem] text-[#]">{desc}</h1>
              </div>

              <div
                className={clsx(
                  "relative",
                  isPageInner ? "h-[14.78rem] pl-[32px]" : "h-[14.25rem]"
                )}
              >
                <img
                  src={isPageInner ? ImgA : ImgB}
                  width={isPageInner ? 315 : 375}
                  height={isPageInner ? 341 : 346}
                  alt="voices-img-a"
                  className={clsx(
                    "absolute z-[-1]",
                    isPageInner ? "mt-[-48px]" : "mt-[-36px]"
                  )}
                />
              </div>

              <div
                className={clsx(
                  "text-[0.875rem] text-[#374259] space-y-[0.25rem] leading-[1.375rem] mb-[1.5rem]",
                  isPageInner && "pt-[1.84rem] pb-[0.5rem]"
                )}
              >
                <p>
                  <span className="text-[#6B7EFF] font-bold">{sns}</span>
                  <br />is coming soon.
                </p>
                <p>
                  Leave your email below for
                  <br />
                  <span className="text-[#6B7EFF] font-bold">
                    early access at launch
                  </span>
                  .
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div
                  className="flex h-12 py-3 px-4 items-center gap-4 self-stretch rounded-xl bg-white"
                  style={{
                    border: `1px solid ${validEmail ? "#374259" : "#DBDBDB"}`,
                  }}
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#999999] text-[#374259]"
                  />
                </div>

                <label
                  onClick={() => setAgreed(!agreed)}
                  className="flex items-center space-x-2 text-[#999999] text-sm  mt-3 mb-6 cursor-pointer"
                >
                  <div
                    className={clsx(
                      "size-6 rounded-full content-center justify-items-center  cursor-pointer transition",
                      agreed ? "bg-[#6875F5]" : "bg-white"
                    )}
                    style={{
                      border: `${!agreed ? "2px solid #DBDBDB" : "none"}`,
                    }}
                  >
                    <CheckIcon width={20} height={20} />
                  </div>
                  <span className="text-[0.875rem] font-medium">
                    I agree to the{" "}
                    <span
                      className="underline underline-offset-2 font-bold"
                      onClick={(e) => {
                        e.stopPropagation(); // 부모 onClick 무시
                        setIsOpen(true);
                      }}
                    >
                      collection and use of my email
                    </span>
                     for early access updates.
                  </span>
                </label>

                <button
                  type="submit"
                  className={clsx(
                    "w-full h-[3.5rem] font-bold  px-4 py-2 rounded-2xl",
                    availSubmit
                      ? "text-white bg-[#6B7EFF]"
                      : "text-[#999] bg-[#EDEDED]"
                  )}
                >
                  Sign up for early access
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <div className="bg-white rounded-2xl w-[327px] relative shadow-xl overflow-hidden">
            {/* 닫기 버튼 */}
            <button
              className="absolute top-4 right-4 text-gray-400"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* 제목 */}
            <h2 className="px-6 font-bold mb-3 mt-6">
              Personal information collection and use
            </h2>

            {/* 본문 */}
            <div className="text-sm text-[#545B77] space-y-4">
              <div className="space-y-3 px-6">
                <div className="space-y-[2px]">
                  <p className="text-[#6B7EFF] font-bold">Purpose</p>
                  <p>To send email updates about using the app.</p>
                </div>
                <div className="space-y-[2px]">
                  <p className="text-[#6B7EFF] font-bold">Information collected</p>
                  <p>Email address</p>
                </div>
                <div className="space-y-[2px]">
                  <p className="text-[#6B7EFF] font-bold">Retention period</p>
                  <p>Up to 60 days after submission.</p>
                  Where a different period is required by law, information will be retained and used in accordance with that law.
                </div>
              </div>
              <div className="bg-[#F7F7FA] px-6 pb-6 pt-5">
                <p className="text-[#999999] font-medium">
                  You may decline consent to the collection and use of your personal information. Consent to the required information is necessary to participate in this event.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export default VoicesPage;
